/**
 * The *stopped by \<ancestor\>* annotation (REQ-UI-144; REQ-PEER-064 / -065).
 *
 * Renders **beside** an entity's state pill, never instead of it. The pill says
 * what the operator set this entity to; this says whether an ancestor is holding
 * it down. Showing either alone is a lie — a started interface under a stopped
 * peer is emitting nothing, and a stopped interface under a stopped peer will
 * still be stopped when the peer starts.
 *
 * # Not a stoplight
 *
 * Suppression is a *configuration* fact, not a health one, so this deliberately
 * does not reach for the status palette: an ancestor stop is a deliberate
 * operator act and painting it amber would put it in the same visual class as a
 * fault. It reads as muted secondary text with an explicit word, which also
 * keeps it clear of REQ-UI-046's colour-alone prohibition — there is no colour
 * carrying meaning here to begin with.
 *
 * Cross-references:
 *   - REQ-UI-144; REQ-PEER-064 (non-destructive cascade), -065 (effective state).
 *   - `peer-effective-state.ts` — where the derivation lives.
 */
import { suppressionLabel, type EffectiveState } from "@/peers/peer-effective-state";

/** Props for {@link PeerSuppressionNote}. */
export interface PeerSuppressionNoteProps {
  resolved: EffectiveState;
  /** Scopes the test id, e.g. ``"interface-sapient"``. */
  testId: string;
}

/** Render the ancestor-suppression annotation, or nothing when none applies. */
export function PeerSuppressionNote({
  resolved,
  testId,
}: PeerSuppressionNoteProps): React.ReactElement | null {
  const label = suppressionLabel(resolved);
  if (label === null) return null;
  return (
    <span
      data-testid={`peer-suppressed-by-${testId}`}
      className="inline-flex items-center gap-1 whitespace-nowrap text-meta text-text-secondary"
    >
      {/* Aria-hidden: the word "Stopped by …" already carries the meaning, so the
          glyph is decoration and announcing it would read as noise. */}
      <span aria-hidden="true">⤒</span>
      {label}
    </span>
  );
}
